// NATIVE
import React, { useEffect, useMemo, useState } from 'react';

// INSTALLED
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { Formik, Form } from 'formik';
import Step from '@mui/material/Step';
import { useDispatch, useSelector } from 'react-redux';

// CUSTOM
import { Button, Card, Divider } from '@mui/material';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import { SnackAlert } from 'components/SnackAlert';
import MultipleSnackbars from 'components/MultipleSnackbars';
import { BreadCrumbsAndTitle } from 'components/generics/BreadCrumbsAndTitle';
import { ModalSessionExpired } from 'components/generics/ModalSessionExpired';
import { EditLaboral } from './components/stepers/steper-editar/EditLaboral';
import { EditFamilia } from './components/stepers/steper-editar/EditFamilia';
import { EditDotacion } from './components/stepers/steper-editar/EditDotacion';
import { EditSeguridadSocial } from './components/stepers/steper-editar/EditSeguridadSocial';
import { EditPruebasIngreso } from './components/stepers/steper-editar/EditPruebasIngreso';
import { EditInformacionBasica } from './components/stepers/steper-editar/EditInformacionBasica';
import editarEmployeFormModel from './formik/editar/editarEmployeFormModel';
import formInitialValues from './formik/editar/formInitialValues';
import validationSchema from './formik/editar/validationSchema';
import { toEditFormInitialValues } from './mappers/employee.mapper';
import {
  editEmployee,
  getEmployeDataCreateStore,
  storeShowDetailEmployeeEditar,
} from 'store/employee';
import { mtDividerUpTable, timeToClosedToast } from '../../../commons';
import { closeSessionGlobal, hasPemissions, setDateValid, validateResponse } from 'commons/utils';
import { ModalViewPdfGlobal } from 'components/modals/viwerPdf/ModalViewPdfGlobal';
import { useResponseHandler } from 'hooks/useResponseHandler';

export const EditEmployee = () => {
  // USE DISPATCH
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id: employeeId }: any = useParams();
  const { employee }: any = useSelector((state: any) => state.employee);

  // DATA MODEL
  const { formId, formField } = editarEmployeFormModel;

  // USE STATE
  const [data, setMsgErrors] = useState([]);

  const [activeStep, setActiveStep] = useState(0);
  const [percentaje_c_Company, setPercentajeCCompany] = useState(2);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [btnText, setBtnText] = React.useState('Editar Empleado');

  const { handleResponse, ResponseComponents, setMsgSnack, setAlertSnack } = useResponseHandler();

  // Estado global del modal PDF
  const [showPdfDetail, setShowPdfDetail] = useState(false);
  const [urlRutPdf, setUrlRutPdf] = useState<string | null>(null);
  const [typeUrl, setTypeUrl] = useState<string | null>(null);

  const handleOpenPdf = (url: string, urlTypeUrl: string) => {
    setUrlRutPdf(url);
    setTypeUrl(urlTypeUrl);
    setShowPdfDetail(true);
  };

  const currentValidationSchema = validationSchema[activeStep];

  const initialValues = useMemo(() => {
    if (
      !employee ||
      Object.keys(employee).length === 0 ||
      (employeeId && String(employee.id || employee.userId) !== String(employeeId))
    ) {
      return {
        ...formInitialValues,
        family: [],
        items: [],
      };
    }
    return toEditFormInitialValues(employee);
  }, [employee, employeeId]);

  // STEPS
  const steps: string[] = [
    'Datos Basicos',
    'Laboral',
    'Seguridad social',
    'Familia',
    'Dotacion',
    'Pruebas de ingreso',
  ];

  const isLastStep = activeStep === steps.length - 1;

  // USE EFFECT
  useEffect(() => {
    getDataInpusToCreateEmploye();

    if (employeeId) {
      fetchEmployeeData();
    }
  }, [employeeId]);

  const fetchEmployeeData = async () => {
    const result = await dispatch(storeShowDetailEmployeeEditar(employeeId));
    if (!result || !result.ok) {
      navigate('/talento-humano/empleado');
    }
  };

  const getDataInpusToCreateEmploye = async () => {
    const response = await dispatch(getEmployeDataCreateStore());
  };

  const lastStep = () => {
    if (activeStep == 0) return;

    setActiveStep(activeStep - 1);
  };

  const _handleSubmit = (values: any, actions: any) => {
    if (isLastStep) {
      _submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const _renderStepContent = (stepIndex: any) => {
    return (
      <>
        <div style={{ display: stepIndex === 0 ? 'block' : 'none' }}>
          <EditInformacionBasica formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 1 ? 'block' : 'none' }}>
          <EditLaboral formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 2 ? 'block' : 'none' }}>
          <EditSeguridadSocial formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 3 ? 'block' : 'none' }}>
          <EditFamilia formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
        <div style={{ display: stepIndex === 4 ? 'block' : 'none' }}>
          <EditDotacion formField={formField} />
        </div>
        <div style={{ display: stepIndex === 5 ? 'block' : 'none' }}>
          <EditPruebasIngreso formField={formField} onOpenPdf={handleOpenPdf} />
        </div>
      </>
    );
  };

  const _submitForm = async (values: any, actions: any) => {
    const havePermissions = await hasPemissions('modulo.thumano', 'modulo.empleados', 'edit');

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Editar) empleado.');

      return false;
    }

    actions.setSubmitting(true);
    setIsSubmitting(true);
    setBtnText('Un momento...');

    // Clonar los valores para no mutar el estado de Formik directamente
    const valuesToSubmit = JSON.parse(JSON.stringify(values));

    // Transformar 'family' para que coincida con el formato del backend
    if (Array.isArray(valuesToSubmit.family)) {
      valuesToSubmit.family = valuesToSubmit.family.map((member: any) => {
        // Renombrar fecha_nacimiento a birthdate
        if (member.fecha_nacimiento) {
          member.birthdate = setDateValid(member.fecha_nacimiento);
          member.fecha_nacimiento = setDateValid(member.fecha_nacimiento);
          // delete member.fecha_nacimiento;
        }
        return member;
      });
    }

    const response = await dispatch(editEmployee(valuesToSubmit, setPercentajeCCompany));

    setIsSubmitting(false);
    setBtnText('Editar Empleado');

    actions.setSubmitting(false);

    await handleResponse(response, 'Editar empleado', true);

    if (!response.ok) {
      return false;
    }

    if (response.ok) {
      const errors: any = {
        msg: response.message,
        status: 'success',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      // actions.resetForm();
      actions.setSubmitting(false);
    }
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      {showPdfDetail && (
        <ModalViewPdfGlobal
          open={showPdfDetail}
          typeUrl={typeUrl}
          onClose={() => setShowPdfDetail(false)}
          pdfUrl={urlRutPdf}
        />
      )}

      <BreadCrumbsAndTitle
        title="Editar Empleado"
        mainModule="Talento Humano"
        parent="Listar Empleados"
        action="/talento-humano/empleados"
      />

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <Stepper activeStep={activeStep}>
        {steps.map((label: any, index: any) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};
          // if (isStepFailed(index)) {
          //   labelProps.optional = (
          //     <Typography variant="caption" color="error">
          //       Faltan campos por diligenciar
          //     </Typography>
          //   );
          //   labelProps.error = true;
          // }

          return (
            <Step key={label}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={currentValidationSchema}
        onSubmit={_handleSubmit}
      >
        {({ values, validateForm, setTouched, dirty }: any) => (
          <Card style={{ marginTop: 50, padding: 20 }}>
            <Form id={formId} noValidate>
              {_renderStepContent(activeStep)}
              <div style={{ display: 'flex', justifyContent: 'end', marginTop: 25 }}>
                {dirty && !isLastStep && (
                  <Button
                    disabled={isSubmitting}
                    style={{ margin: 5 }}
                    variant="outlined"
                    color="success"
                    onClick={async () => {
                      const errors = await validateForm();
                      if (Object.keys(errors).length === 0) {
                        setActiveStep(steps.length - 1);
                      } else {
                        setTouched(errors);
                      }
                    }}
                  >
                    Ir al final para guardar
                  </Button>
                )}

                {activeStep > 0 && (
                  <Button
                    style={{ margin: 5 }}
                    variant="contained"
                    onClick={lastStep}
                    color="primary"
                  >
                    Atras
                  </Button>
                )}
                <Button
                  disabled={isSubmitting}
                  style={{ margin: 5 }}
                  type="submit"
                  variant="contained"
                  color={isLastStep ? 'success' : 'primary'}
                >
                  {isLastStep ? btnText : 'Siguiente'}
                </Button>
              </div>
            </Form>
          </Card>
        )}
      </Formik>
    </Box>
  );
};
